import type { DocumentNode } from 'graphql';
import type {
  PlaceBidMutation,
  PlaceBidMutationVariables,
  PlaceTransferOfferMutation,
  PlaceTransferOfferMutationVariables,
  Player,
} from '../../../types/graphql-generated';
import { GET_FANTASY_LEAGUE } from '../../../graphql/queries/fantasyLeague';
import { GET_VISIBLE_BIDS } from '../../../graphql/queries/visibleBids';
import { GET_VISIBLE_TRANSFER_OFFERS } from '../../../graphql/queries/visibleTransferOffers';

type PlaceBidFn = (options: {
  variables: PlaceBidMutationVariables;
  refetchQueries?: Array<{ query: DocumentNode; variables?: Record<string, unknown> }>;
}) => Promise<{ data?: PlaceBidMutation | null }>;

type PlaceTransferOfferFn = (options: {
  variables: PlaceTransferOfferMutationVariables;
  refetchQueries?: Array<{ query: DocumentNode; variables?: Record<string, unknown> }>;
}) => Promise<{ data?: PlaceTransferOfferMutation | null }>;

interface ExistingBid {
  id: string;
  amount: number;
}

interface ExistingOffer {
  id: string;
  amount: number;
}

interface MarketEntryInput {
  id: string;
  price: number;
  player: Player;
  seller?: { id: string; user: { id: string; fullName?: string | null } } | null;
}

export interface OfferTargetMutations {
  placeBid: PlaceBidFn;
  placeTransferOffer: PlaceTransferOfferFn;
}

export interface OfferTarget {
  getPlayer(): Player;
  getOfferLabel(): string;
  getInitialAmount(): string;
  isEditing(): boolean;
  isFreeAgent(): boolean;
  getPrice(): number | undefined;
  getBudget(): number | undefined;
  validate(amountValue: number): string[];
  execute(amount: number, leagueId: string, mutations: OfferTargetMutations): Promise<string[]>;
}

class MarketBidTarget implements OfferTarget {
  private marketEntry: MarketEntryInput;
  private existingBid: ExistingBid | null;

  constructor(marketEntry: MarketEntryInput, existingBid: ExistingBid | null) {
    this.marketEntry = marketEntry;
    this.existingBid = existingBid;
  }

  getPlayer(): Player {
    return this.marketEntry.player;
  }

  getOfferLabel(): string {
    return 'oferta';
  }

  getInitialAmount(): string {
    return this.existingBid
      ? this.existingBid.amount.toString()
      : this.marketEntry.price.toString();
  }

  isEditing(): boolean {
    return !!this.existingBid;
  }

  isFreeAgent(): boolean {
    return !this.marketEntry.seller;
  }

  getPrice(): number | undefined {
    return this.marketEntry.price;
  }

  getBudget(): number | undefined {
    return undefined;
  }

  validate(amountValue: number): string[] {
    if (this.isFreeAgent() && amountValue < this.marketEntry.price) {
      const label = this.getOfferLabel();
      return [
        `Para agentes libres, la ${label} debe ser igual o mayor al precio solicitado (${this.marketEntry.price.toLocaleString('es-ES')}€)`,
      ];
    }
    return [];
  }

  async execute(amount: number, leagueId: string, mutations: OfferTargetMutations): Promise<string[]> {
    const { data } = await mutations.placeBid({
      variables: { marketEntryId: this.marketEntry.id, amount },
      refetchQueries: [
        { query: GET_FANTASY_LEAGUE, variables: { id: leagueId } },
        { query: GET_VISIBLE_BIDS, variables: { fantasyLeagueId: leagueId } },
      ],
    });
    return data?.placeBid?.errors ?? [];
  }
}

class DirectTransferTarget implements OfferTarget {
  private player: Player;
  private currentMemberBudget: number;
  private existingOffer: ExistingOffer | null;

  constructor(player: Player, currentMemberBudget: number, existingOffer: ExistingOffer | null) {
    this.player = player;
    this.currentMemberBudget = currentMemberBudget;
    this.existingOffer = existingOffer;
  }

  getPlayer(): Player {
    return this.player;
  }

  getOfferLabel(): string {
    return 'oferta directa';
  }

  getInitialAmount(): string {
    return this.existingOffer ? this.existingOffer.amount.toString() : '';
  }

  isEditing(): boolean {
    return !!this.existingOffer;
  }

  isFreeAgent(): boolean {
    return false;
  }

  getPrice(): number | undefined {
    return undefined;
  }

  getBudget(): number | undefined {
    return this.currentMemberBudget;
  }

  validate(amountValue: number): string[] {
    if (amountValue > this.currentMemberBudget) {
      return [
        `No tienes presupuesto suficiente. Disponible: ${this.currentMemberBudget.toLocaleString('es-ES')}€`,
      ];
    }
    return [];
  }

  async execute(amount: number, leagueId: string, mutations: OfferTargetMutations): Promise<string[]> {
    const { data } = await mutations.placeTransferOffer({
      variables: { fantasyLeagueId: leagueId, playerId: this.player.id, amount },
      refetchQueries: [
        { query: GET_VISIBLE_TRANSFER_OFFERS, variables: { fantasyLeagueId: leagueId } },
      ],
    });
    return data?.placeTransferOffer?.errors ?? [];
  }
}

export class OfferTargetFactory {
  static fromMarketEntry(
    marketEntry: MarketEntryInput,
    existingBid: ExistingBid | null = null
  ): OfferTarget {
    return new MarketBidTarget(marketEntry, existingBid);
  }

  static fromPlayer(
    player: Player,
    currentMemberBudget: number,
    existingOffer: ExistingOffer | null = null
  ): OfferTarget {
    return new DirectTransferTarget(player, currentMemberBudget, existingOffer);
  }
}
